import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logoMp from '../img/mp-icono.png';
import metaPixelService from '../services/metaPixelService';
import firebaseAnalyticsService from '../services/firebaseAnalyticsService';
import { getEventConfig, CUSTOM_EVENTS } from '../config/metaPixelConfig';
import TermsModal from '../components/TermsModal';
import CalendlyModal from '../components/CalendlyModal';
import CampoCuit from '../components/CampoCuit';
import CampoDireccion from '../components/CampoDireccion';
import DemoTransferencias from '../components/DemoTransferencias';
import RegistroHeader from '../components/RegistroHeader';
import './Registro.css';
import './Negocio.css';
import { DIRECCION_VACIA, componer, faltantes } from '../utils/direccion';
import { AYUDA_ARGENTINA, normalizarTelefono, telefonoLegible } from '../utils/telefono';

// En la Mac puede apuntar al emulador (REACT_APP_FUNCTIONS_URL, ver README "Probar en local").
const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-transferisok.cloudfunctions.net';

// Panel derecho: qué pasa en el paso siguiente, cuando autoriza Mercado Pago.
const PUNTOS_MERCADO_PAGO = [
    ['Solo lectura.', 'Vemos los pagos que recibís, nada más.'],
    ['No movemos plata.', 'No podemos pagar, transferir ni ingresar dinero.'],
    ['No guardamos tu clave.', 'Autorizás desde Mercado Pago, con tu usuario.'],
];

function Negocio() {
    const [businessData, setBusinessData] = useState({
        DNI_CUIT: '',
        phone: '',
        branches: '',
        sector: '',
        otherSector: '',
        employees: '',
        mainAddress: ''
    });
    // Titular del CUIT según ARCA (ver CampoCuit) y dirección desglosada (ver CampoDireccion)
    const [titular, setTitular] = useState({ estado: 'vacio' });
    const [direccion, setDireccion] = useState(DIRECCION_VACIA);
    const [countryCode, setCountryCode] = useState('+54'); // Código de país por defecto (Argentina)
    const [telefonoTocado, setTelefonoTocado] = useState(false);
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [showOtherSector, setShowOtherSector] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showCalendlyModal, setShowCalendlyModal] = useState(false);
    const [formProgress, setFormProgress] = useState(0);
    const [viewContentTracked, setViewContentTracked] = useState(false);
    const [pageLoadTime] = useState(Date.now()); // Para medir tiempo en página
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const navigate = useNavigate();

    // Lista de países con códigos y URLs de banderas
    const countries = [
        { code: '+54', abbr: 'ar', shortName: 'ARG' },
        { code: '+56', abbr: 'cl', shortName: 'CHL' },
        { code: '+55', abbr: 'br', shortName: 'BRA' },
        { code: '+598', abbr: 'uy', shortName: 'URY' }
    ];

    const sectors = [
        "Indumentaria / Calzado",
        "Bar / Restaurante / Cafeteria",
        "Peluquerías / Barbería",
        "Venta de alimentos y bebidas",
        "Electrodomésticos / Electrónica",
        "Mueblería y decoración",
        "Farmacia / Perfumería",
        "Automotriz",
        "Educación",
        "Librerías",
        "Gimnasio",
        "Centro médico",
        "Hotelería",
        "Servicios profesionales",
        "Tienda de mascotas",
        "Textil / Mercería",
        "Ferretería",
        "Otro"
    ];

    // Desplazar la página hacia arriba al cargar, especialmente después de navegar desde RegisterData
    useEffect(() => {
        const shouldScrollToTop = localStorage.getItem('scrollToTop');
        if (shouldScrollToTop === 'true') {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            // Eliminar la indicación para que no se desplace en futuras visitas a la página
            localStorage.removeItem('scrollToTop');
        }
    }, []);

    // Trackear abandono: cerró la página con el formulario a medio completar
    useEffect(() => {
        const handlePageHide = () => {
            const campos = [
                businessData.DNI_CUIT,
                businessData.phone,
                businessData.branches,
                businessData.sector,
                businessData.employees,
                businessData.mainAddress
            ];
            const completedFields = campos.filter(campo => campo && campo.toString().trim() !== '').length;

            if (completedFields > 0 && completedFields < campos.length) {
                firebaseAnalyticsService.trackFormAbandonment(
                    'business_data',
                    completedFields,
                    campos.length,
                    Date.now() - pageLoadTime
                );
            }
        };

        window.addEventListener('pagehide', handlePageHide);
        return () => {
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [businessData, pageLoadTime]);

    // Trackear ViewContent cuando se carga la página de datos del negocio
    useEffect(() => {
        if (!viewContentTracked) {
            firebaseAnalyticsService.trackRegistro('registro_paso2_inicio');

            // Trackear que el usuario llegó al segundo paso del registro
            const viewContentConfig = getEventConfig('viewContent', 'registrationStep2');
            metaPixelService.trackViewContent({
                ...viewContentConfig,
                form_step: 'business_data'
            });

            // Recuperar datos del usuario del localStorage para contexto
            const userData = localStorage.getItem('pendingUserData');
            if (userData) {
                const parsedUserData = JSON.parse(userData);
                metaPixelService.updateUserData({
                    email: parsedUserData.email,
                    fullName: parsedUserData.fullName,
                    businessName: parsedUserData.businessName
                });
            }

            setViewContentTracked(true);
            console.log('ViewContent trackeado en Negocio.js');
        }
    }, [viewContentTracked]);

    // Trackear progreso del formulario
    useEffect(() => {
        const totalFields = 7; // DNI_CUIT, phone, branches, sector, employees, mainAddress, countryCode
        const completedFields = [
            businessData.DNI_CUIT,
            businessData.phone,
            businessData.branches,
            businessData.sector,
            businessData.employees,
            businessData.mainAddress,
            countryCode
        ].filter(field => field && field.toString().trim() !== '').length;

        const newProgress = Math.round((completedFields / totalFields) * 100);

        if (newProgress !== formProgress && newProgress > 0) {
            setFormProgress(newProgress);

            // Trackear progreso del formulario
            metaPixelService.trackFormProgress('business_data', completedFields, totalFields);
        }
    }, [businessData, formProgress, countryCode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setBusinessData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'sector') {
            setShowOtherSector(value === 'Otro');
        }

        // ✅ OPTIMIZADO: NO trackear cada onChange, solo datos importantes

        // Actualizar datos del usuario para tracking
        if (name === 'phone' && normalizarTelefono(countryCode, value)) {
            metaPixelService.updateUserData({ phone: normalizarTelefono(countryCode, value) });
        }

        if (name === 'sector' && value) {
            metaPixelService.updateUserData({ sector: value });
        }

        if (name === 'employees' && value) {
            metaPixelService.updateUserData({ employees: parseInt(value) });
        }

        if (name === 'branches' && value) {
            metaPixelService.updateUserData({ branches: parseInt(value) });
        }
    };

    // ✅ NUEVO: Trackear solo cuando el usuario ENTRA al campo
    const handleFieldFocus = (fieldName) => {
        metaPixelService.trackFormInteraction('business_data', 'field_focus', fieldName);
    };

    // ✅ NUEVO: Trackear solo cuando el usuario SALE del campo
    const handleFieldBlur = (fieldName, value) => {
        if (value && value.toString().trim() !== '') {
            metaPixelService.trackFormInteraction('business_data', 'field_completed', fieldName);
        }
    };

    const handleCuitChange = (cuit) => {
        setBusinessData(prev => ({ ...prev, DNI_CUIT: cuit }));
        if (cuit) {
            metaPixelService.updateUserData({ dni_cuit: cuit });
        }
    };

    const handleAddressChange = (address) => {
        setBusinessData(prev => ({ ...prev, mainAddress: address }));
        if (address) {
            metaPixelService.updateUserData({ mainAddress: address });
        }
    };

    const handleTermsChange = (e) => {
        setTermsAccepted(e.target.checked);

        // Trackear interacción con términos y condiciones
        metaPixelService.trackFormInteraction('business_data', 'terms_interaction', e.target.checked ? 'accepted' : 'declined');
    };

    const handleShowTerms = (e) => {
        e.preventDefault();
        setShowTermsModal(true);

        // Trackear que el usuario abrió los términos
        metaPixelService.trackFormInteraction('business_data', 'terms_view', 'modal_opened');
    };

    const handleCloseTermsModal = () => {
        setShowTermsModal(false);
    };

    // El teléfono como se guarda (formato internacional), o '' si el número no está completo.
    const telefono = normalizarTelefono(countryCode, businessData.phone);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Trackear inicio del envío del formulario
        metaPixelService.trackFormInteraction('business_data', 'form_submit', 'submit_button');

        try {
            // Validar campos requeridos
            if (!businessData.DNI_CUIT || !businessData.phone || !businessData.branches || !businessData.sector || !businessData.employees || !businessData.mainAddress) {
                setError('Completá todos los campos.');
                setLoading(false);

                // Trackear error de validación
                metaPixelService.trackCustomEvent(CUSTOM_EVENTS.FORM_VALIDATION_ERROR, {
                    form_step: 'business_data',
                    error_type: 'required_fields_missing',
                    missing_fields: [
                        !businessData.DNI_CUIT && 'DNI_CUIT',
                        !businessData.phone && 'phone',
                        !businessData.branches && 'branches',
                        !businessData.sector && 'sector',
                        !businessData.employees && 'employees',
                        !businessData.mainAddress && 'mainAddress'
                    ].filter(Boolean)
                });

                return;
            }

            // El CUIT tiene que existir en ARCA. Si ARCA no contestó (sin_respuesta) se
            // sigue igual: no se pierde un registro porque ARCA esté caído.
            const errorCuit = {
                vacio: 'El CUIT tiene 11 números.',
                invalido: 'El CUIT no es válido. Revisá los números.',
                buscando: 'Estamos verificando el CUIT en ARCA, esperá un segundo.',
                no_encontrado: 'No encontramos ese CUIT en ARCA. Revisá el número.'
            }[titular.estado];
            if (errorCuit) {
                setError(errorCuit);
                setLoading(false);

                metaPixelService.trackCustomEvent(CUSTOM_EVENTS.FORM_VALIDATION_ERROR, {
                    form_step: 'business_data',
                    error_type: `cuit_${titular.estado}`
                });

                return;
            }

            if (!telefono) {
                setError(`Revisá el teléfono: ${countryCode === '+54' ? AYUDA_ARGENTINA : 'falta el número completo.'}`);
                setTelefonoTocado(true);
                setLoading(false);

                metaPixelService.trackCustomEvent(CUSTOM_EVENTS.FORM_VALIDATION_ERROR, {
                    form_step: 'business_data',
                    error_type: 'phone_invalid'
                });

                return;
            }

            const faltaDireccion = faltantes(direccion);
            if (faltaDireccion.length > 0) {
                setError(`Completá la dirección del negocio: falta ${faltaDireccion.join(', ').replace(/, ([^,]*)$/, ' y $1')}.`);
                setLoading(false);

                metaPixelService.trackCustomEvent(CUSTOM_EVENTS.FORM_VALIDATION_ERROR, {
                    form_step: 'business_data',
                    error_type: 'address_incomplete',
                    missing_fields: faltaDireccion
                });

                return;
            }

            // La dirección tiene que estar en Google Maps o confirmada por el cliente
            // (CampoDireccion). Si Google no contesta queda en sin_google y se sigue igual.
            if (!direccion.verificacion) {
                setError('Revisá la dirección del negocio: elegí una de las opciones que encontramos en Google Maps o confirmá que está bien escrita.');
                setLoading(false);

                metaPixelService.trackCustomEvent(CUSTOM_EVENTS.FORM_VALIDATION_ERROR, {
                    form_step: 'business_data',
                    error_type: 'address_unverified'
                });

                return;
            }

            // Validar que se hayan aceptado los términos y condiciones
            if (!termsAccepted) {
                setError('Aceptá los términos y condiciones para seguir.');
                setLoading(false);

                // Trackear error de validación de términos
                metaPixelService.trackCustomEvent(CUSTOM_EVENTS.FORM_VALIDATION_ERROR, {
                    form_step: 'business_data',
                    error_type: 'terms_not_accepted'
                });

                return;
            }

            // Preparar los datos a guardar
            const businessDataToSave = {
                DNI_CUIT: businessData.DNI_CUIT,
                phone: telefono,
                branches: businessData.branches,
                sector: businessData.sector === 'Otro' ? businessData.otherSector : businessData.sector,
                otherSector: businessData.sector === 'Otro' ? businessData.otherSector : null,
                employees: businessData.employees,
                // El nombre del titular y si es persona o empresa no viajan: el alta los
                // toma del padrón de ARCA que ya consultó CampoCuit.
                mainAddress: componer(direccion),
                direccion,
                termsAccepted: true,
                termsAcceptedAt: new Date().toISOString()
            };

            // Los datos del primer paso (RegisterData) siguen en el localStorage.
            const pendingUserData = JSON.parse(localStorage.getItem('pendingUserData') || 'null');
            if (!pendingUserData || !pendingUserData.email) {
                setError('No encontramos los datos del primer paso. Volvé a empezar el registro.');
                setLoading(false);
                setTimeout(() => {
                    navigate('/registro');
                }, 3000);
                return;
            }

            // El backend guarda los datos y arma el link a Mercado Pago con un state
            // firmado: a la vuelta, el alta los lee de ahí y no del navegador.
            const respuesta = await fetch(`${FUNCTIONS_URL}/iniciarAlta`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    usuario: pendingUserData,
                    negocio: businessDataToSave,
                    analitica: await firebaseAnalyticsService.identificadores()
                })
            });
            const alta = await respuesta.json().catch(() => ({}));
            if (!respuesta.ok || !alta.authUrl) {
                setError(alta.message || 'No pudimos iniciar el registro. Intentá de nuevo en un momento.');
                setLoading(false);

                const codigo = alta.error || `http_${respuesta.status}`;
                firebaseAnalyticsService.trackRegistro('registro_error', { paso: 'negocio', codigo });
                metaPixelService.trackCustomEvent(CUSTOM_EVENTS.FORM_ERROR, {
                    form_step: 'business_data',
                    error_type: codigo
                });

                return;
            }

            // Terminó el formulario y sale a autorizar. El registro todavía no está completo:
            // CompleteRegistration y sign_up se mandan a la vuelta, en AuthCallback.
            // El rubro va de la lista fija: lo que escriba en "Otro" es texto libre.
            firebaseAnalyticsService.trackRegistro('registro_paso2_completo', {
                rubro: businessData.sector,
                sucursales: businessData.branches,
                empleados: businessData.employees
            });
            metaPixelService.trackCustomEvent(CUSTOM_EVENTS.REGISTRO_HACIA_MP, {
                form_step: 'business_data_complete'
            });

            // Pequeña pausa para asegurar que el evento se envíe antes de la redirección
            await new Promise(resolve => setTimeout(resolve, 500));

            window.location.href = alta.authUrl;

        } catch (error) {
            console.error('Error al procesar los datos del negocio:', error);
            setError(`Ocurrió un error: ${error.message}`);

            // Trackear error en el formulario
            firebaseAnalyticsService.trackRegistro('registro_error', { paso: 'negocio', codigo: 'error_inesperado' });
            metaPixelService.trackCustomEvent(CUSTOM_EVENTS.FORM_ERROR, {
                form_step: 'business_data',
                error_message: error.message,
                error_type: 'business_data_error'
            });
        } finally {
            setLoading(false);
        }
    };

    const pais = countries.find(c => c.code === countryCode) || countries[0];

    return (
        <div className="registro-pagina">
            <RegistroHeader onAgendar={() => setShowCalendlyModal(true)} />

            <main className="registro-pantalla">
                <div className="registro-tarjeta">
                    <section className="registro-form-lado">
                        <h1 className="registro-titulo">Datos de tu negocio</h1>
                        <p className="registro-bajada">Con esto creamos tu cuenta. Después autorizás Mercado Pago y listo.</p>

                        <form onSubmit={handleSubmit} className="registro-formulario">
                            <CampoCuit
                                value={businessData.DNI_CUIT}
                                onChange={handleCuitChange}
                                titular={titular}
                                onTitular={setTitular}
                                onFocus={() => handleFieldFocus('DNI_CUIT')}
                                onBlur={(e) => handleFieldBlur('DNI_CUIT', e.target.value)}
                            />

                            <div className="registro-fila">
                                <div className="registro-campo">
                                    <label htmlFor="negocio-telefono" className="registro-label">Teléfono</label>
                                    <div className="negocio-telefono">
                                        <button
                                            type="button"
                                            className="negocio-pais"
                                            onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                            aria-haspopup="listbox"
                                            aria-expanded={showCountryDropdown}
                                            aria-label={`Código de país ${pais.code}`}
                                        >
                                            <img src={`https://flagcdn.com/w40/${pais.abbr}.png`} alt="" />
                                            <span>{pais.code}</span>
                                            <svg className={showCountryDropdown ? 'abierto' : ''} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" aria-hidden="true">
                                                <path stroke="#64748b" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 8l4 4 4-4" />
                                            </svg>
                                        </button>
                                        <input
                                            id="negocio-telefono"
                                            type="tel"
                                            name="phone"
                                            inputMode="tel"
                                            autoComplete="tel-national"
                                            value={businessData.phone}
                                            onChange={handleChange}
                                            onFocus={() => handleFieldFocus('phone')}
                                            onBlur={(e) => {
                                                setTelefonoTocado(true);
                                                handleFieldBlur('phone', e.target.value);
                                            }}
                                            placeholder={countryCode === '+54' ? '351 789 1695' : ''}
                                            aria-describedby="negocio-telefono-aviso"
                                            required
                                        />
                                        {showCountryDropdown && (
                                            <>
                                                {/* Fondo invisible para cerrar al tocar afuera */}
                                                <div className="negocio-pais-cerrar" onClick={() => setShowCountryDropdown(false)} />
                                                <ul className="negocio-pais-lista" role="listbox">
                                                    {countries.map(country => (
                                                        <li
                                                            key={country.code}
                                                            role="option"
                                                            aria-selected={countryCode === country.code}
                                                            onClick={() => {
                                                                setCountryCode(country.code);
                                                                setShowCountryDropdown(false);
                                                            }}
                                                        >
                                                            <img src={`https://flagcdn.com/w40/${country.abbr}.png`} alt="" />
                                                            <strong>{country.code}</strong>
                                                            <span>{country.shortName}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        )}
                                    </div>
                                    {/* Lo que se va a guardar, para que se vea si quedó mal escrito. */}
                                    {telefono ? (
                                        <div id="negocio-telefono-aviso" className="negocio-telefono-aviso negocio-telefono-ok">
                                            Te escribimos por WhatsApp al {telefonoLegible(telefono)}
                                        </div>
                                    ) : (businessData.phone && (telefonoTocado || businessData.phone.replace(/\D/g, '').length > 13)) ? (
                                        <div id="negocio-telefono-aviso" className="negocio-telefono-aviso negocio-telefono-error">
                                            {countryCode === '+54' ? AYUDA_ARGENTINA : 'Falta el número completo.'}
                                        </div>
                                    ) : (
                                        <div id="negocio-telefono-aviso" className="negocio-telefono-aviso">
                                            {countryCode === '+54' ? 'Con código de área, sin 0 ni 15.' : 'Con código de área.'}
                                        </div>
                                    )}
                                </div>

                                <div className="registro-campo">
                                    <label htmlFor="negocio-rubro" className="registro-label">Rubro</label>
                                    <select
                                        id="negocio-rubro"
                                        name="sector"
                                        value={businessData.sector}
                                        onChange={handleChange}
                                        onFocus={() => handleFieldFocus('sector')}
                                        onBlur={(e) => handleFieldBlur('sector', e.target.value)}
                                        className="registro-input registro-select"
                                        required
                                    >
                                        <option value="">Elegí un rubro</option>
                                        {sectors.map(sector => (
                                            <option key={sector} value={sector}>{sector}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {showOtherSector && (
                                <div className="registro-campo">
                                    <label htmlFor="negocio-otro-rubro" className="registro-label">¿Cuál es el rubro?</label>
                                    <input
                                        id="negocio-otro-rubro"
                                        type="text"
                                        name="otherSector"
                                        value={businessData.otherSector}
                                        onChange={handleChange}
                                        onFocus={() => handleFieldFocus('otherSector')}
                                        onBlur={(e) => handleFieldBlur('otherSector', e.target.value)}
                                        className="registro-input"
                                        placeholder="Ej.: Vinoteca"
                                        required
                                    />
                                </div>
                            )}

                            <div className="registro-fila negocio-fila-numeros">
                                <div className="registro-campo">
                                    <label htmlFor="negocio-sucursales" className="registro-label">Sucursales</label>
                                    <input
                                        id="negocio-sucursales"
                                        type="number"
                                        min="1"
                                        inputMode="numeric"
                                        name="branches"
                                        value={businessData.branches}
                                        onChange={handleChange}
                                        onFocus={() => handleFieldFocus('branches')}
                                        onBlur={(e) => handleFieldBlur('branches', e.target.value)}
                                        className="registro-input"
                                        placeholder="Ej.: 1"
                                        required
                                    />
                                </div>
                                <div className="registro-campo">
                                    <label htmlFor="negocio-empleados" className="registro-label">Empleados</label>
                                    <input
                                        id="negocio-empleados"
                                        type="number"
                                        min="0"
                                        inputMode="numeric"
                                        name="employees"
                                        value={businessData.employees}
                                        onChange={handleChange}
                                        onFocus={() => handleFieldFocus('employees')}
                                        onBlur={(e) => handleFieldBlur('employees', e.target.value)}
                                        className="registro-input"
                                        placeholder="Ej.: 4"
                                        required
                                    />
                                </div>
                            </div>

                            <CampoDireccion
                                linea={businessData.mainAddress}
                                onLinea={handleAddressChange}
                                direccion={direccion}
                                onDireccion={setDireccion}
                                onFocus={() => handleFieldFocus('mainAddress')}
                                onBlur={(e) => handleFieldBlur('mainAddress', e.target.value)}
                            />

                            <label className="negocio-terminos">
                                <input type="checkbox" checked={termsAccepted} onChange={handleTermsChange} />
                                <span>
                                    Acepto los{' '}
                                    <button type="button" onClick={handleShowTerms}>términos y condiciones</button>
                                    {' '}del servicio
                                </span>
                            </label>

                            {error && <div className="registro-error" role="alert">{error}</div>}

                            <button type="submit" className="registro-boton" disabled={loading}>
                                {loading && <span className="registro-spinner" aria-hidden="true" />}
                                {loading ? 'Procesando…' : 'Crear cuenta'}
                            </button>
                        </form>

                        <p className="registro-ingresar">
                            ¿Necesitás ayuda?{' '}
                            <button type="button" className="negocio-ayuda" onClick={() => setShowCalendlyModal(true)}>
                                Agendá una llamada
                            </button>
                        </p>
                    </section>

                    <aside className="registro-info-lado negocio-info-lado" aria-hidden="true">
                        <div className="negocio-sello-mp">
                            <img src={logoMp} alt="" />
                            Se conecta con Mercado Pago
                        </div>
                        <DemoTransferencias
                            titulo="Después conectás Mercado Pago, solo para ver lo que entra"
                            resaltar="solo para ver lo que entra"
                            puntos={PUNTOS_MERCADO_PAGO}
                        />
                    </aside>
                </div>
            </main>

            <TermsModal
                isOpen={showTermsModal}
                onClose={handleCloseTermsModal}
            />

            <CalendlyModal
                isOpen={showCalendlyModal}
                onClose={() => setShowCalendlyModal(false)}
            />
        </div>
    );
}

export default Negocio;
